/* @flow */
/* eslint-env es6,browser */

import adconfig from './adconfig';
import google from './adserver/googleAS';
import staticAS from './adserver/staticas';
import { logError, logDebug } from './utils/logger';
import { extend, difference, intersect } from './utils/coreutils';

import bm from './bidmanager';
import getCVars from './utils/gputils';

/**
 * @description Implements ad server functionality
 * @class
 * @exports adserver
 * @requires  module:coreutils
 * @requires  module:adserver/google
 * @requires  module:adserver/staticas
 * @requires  module:adconfig
 * @requires  module:bidmanager
 * @requires module:logger
 */
class _adserver {
  _doc: Document;

  _win: WindowProxy;

  _adservers: Array<adServerInterface>;

  _staticCustomVars: CustomVars;

  _customVars: CustomVars;

  constructor() {
    this._doc = document;
    this._win = window;
    this._staticCustomVars = {};
    this._customVars = {};
  }

  /**
   * Initialize the object
   *
   * @param {window} w - window object
   * @param {Document} d - document object
   * @param {module:globalParams.globalParams} g - global param object
   * @returns {undefined}
   */
  init(w: WindowProxy, d: Document, g: globalParam) {
    this._doc = d;
    this._win = w;
    this._staticCustomVars = g.customVars;
    this._adservers = [staticAS, google].filter(v => v._isSupported());
    this._adservers.forEach(as => as.init(w, d, g));
    // initialize bidmanager
    bm.init(g);
  }

  /**
   * Update config - called adserver updateConfig
   *
   * @external adServerConfig
   * @param {adServerConfig} o - object with config for each adserver from adconfig
   * @returns {undefined}
   */
  updateConfig(o: adServerConfig) {
    this._adservers.forEach(as => as.updateConfig(o[as.getName()]));
  }

  /**
   * get custom variables
   *
   * @returns {object} - Custom variables to send along with ad request
   * @access private
   */
  _getCustomVars(): CustomVars {
    const ret = getCVars(this._win);
    // merge user provided customVar
    extend(true, ret, this._customVars);
    return ret;
  }

  /**
   * load ads
   *
   * @param {Array<string>} posList - Ad position list
   * @external hbPartnerData
   * @param {hbPartnerData} o - HB data
   * @param {boolean} bRotation - For ad rotation?
   * @returns {undefined}
   * @access private
   */
  _loadAds(
    posList: Array<string> = [],
    o: hbPartnerData = {},
    bRotation: boolean = false
  ) {
    const cumPosList = this._adservers.flatMap(as => {
      const asName = as.getName();
      const asPosList = posList.filter(
        pos => adconfig.getPosSettings(pos).as === asName
      );
      if (asPosList.length) {
        as.loadAds(asPosList, o, this._getCustomVars(), bRotation);
      }
      return asPosList;
    });
    const misPos = difference(posList, cumPosList).join(',');
    if (misPos.length) {
      logError(`adserver is not configured for ${misPos}`);
    }
  }

  clearAdRequests() {
    // invoke the same in each listed adserver
    this._adservers.forEach(as => as.clearAdRequests());
  }

  /**
   * Bid and load ads
   *
   * @param {string} pageName - Page name
   * @param {Array<string>} posListArr - List of ad positions
   * @param {boolean} bRotation - For ad rotation?
   * @returns {undefined}
   */
  loadAds(pageName: string, posListArr: Array<string>, bRotation: boolean) {
    const activeBidPos = intersect(posListArr, bm.getBidPos(pageName));
    const inactiveBidPos = difference(posListArr, activeBidPos);
    logDebug(
      `active bid pos:${activeBidPos.join(
        ','
      )} inactive bid pos:${inactiveBidPos.join(',')}`
    );

    // load inactive static ad positions immediately
    if (inactiveBidPos.length) {
      this._loadAds(inactiveBidPos, {}, bRotation);
    }

    // perform header bidding for remaining positions
    if (activeBidPos.length > 0) {
      bm.getBids(activeBidPos, pageName, bRotation).then(
        o => this._loadAds(activeBidPos, o, bRotation),
        () => this._loadAds(activeBidPos, {}, bRotation)
      );
    }
  }

  /**
   *
   * Render ad from the bidder
   *
   * @param {window} win - IFrame window object
   * @param {Document} doc - IFrame document
   * @param {string} pos - Ad position
   * @param {string} partner - Partner won the bid
   * @param {string} param - Extra params
   * @returns {undefined}
   * @memberof _adserver
   */
  renderBidAds(
    win: WindowProxy,
    doc: Document,
    pos: string,
    partner: string,
    param: string
  ) {
    bm.renderAd(win, doc, pos, partner, param, adconfig.getPosSettings(pos));
  }

  /**
   * set custom var
   *
   * @param {string} k - Custom Var key
   * @param {object} v - Custom var value
   * @returns {undefined}
   * @access private
   */
  setCustomVar(k: string, v: mixed) {
    const key = k || '';
    if (key.length) {
      this._customVars[key] = v;
    }
  }
}

// export singleton object as default for this module
const as: _adserver = new _adserver();
export default as;
